import qrCodeGen, { QRCodeRenderersOptions } from 'qrcode'
import { forwardRef, Ref, useEffect, useRef } from 'react'
import { useMergedRefs } from '../hooks/use-merged-refs'

export interface QrCodeProps extends QRCodeRenderersOptions {
    data: string
    label?: string
}

export const QrCode = forwardRef(
    ({ data, label, ...options }: QrCodeProps, ref: Ref<HTMLCanvasElement>) => {
        const canvasRef = useRef<HTMLCanvasElement>(null)
        const mergedRefs = useMergedRefs(ref, canvasRef)

        useEffect(
            () =>
                void (async () => {
                    const canvas = canvasRef.current
                    if (!canvas) return
                    const ctx = canvas.getContext('2d')
                    if (!ctx) throw new Error('Canvas context not available')

                    const moduleSize = 4
                    const margin = options?.margin ?? 0
                    const fontSize = 18
                    const labelHeight = label ? 45 : 0

                    const tempCanvas = document.createElement('canvas')

                    await qrCodeGen.toCanvas(tempCanvas, data, {
                        ...options,
                        errorCorrectionLevel: 'L',
                        scale: moduleSize,
                        margin,
                    })

                    const qrWidth = tempCanvas.width
                    const qrHeight = tempCanvas.height

                    canvas.width = qrWidth
                    canvas.height = qrHeight + labelHeight

                    ctx.fillStyle = '#fff'
                    ctx.fillRect(0, 0, canvas.width, canvas.height)

                    ctx.imageSmoothingEnabled = false
                    ctx.drawImage(tempCanvas, 0, 0)

                    if (label) {
                        const bottomPadding = 10
                        ctx.font = `${fontSize}px Arial`
                        ctx.fillStyle = '#000'
                        ctx.textAlign = 'center'
                        ctx.fillText(
                            label,
                            qrWidth / 2,
                            qrHeight + labelHeight - bottomPadding,
                        )
                    }
                })(),
            [data, label, options],
        )

        return <canvas ref={mergedRefs} />
    },
)
